import apiClient from './api';

export interface Compagnie {
  id: number;
  codeIata: string;
  nom: string;
  actif: boolean;
}

export interface CreateCompagnieDTO {
  codeIata: string;
  nom: string;
}

export interface UpdateCompagnieDTO {
  codeIata?: string;
  nom?: string;
}

export interface CompagnieStats {
  total: number;
  actives: number;
  inactives: number;
}

export interface ImportResult {
  created: number;
  updated: number;
  errors: string[];
}

const BASE_URL = '/compagnies';

/**
 * Service API pour les compagnies aeriennes
 */
export const compagnieService = {
  /**
   * Liste des compagnies
   * @param all - si true, inclut les compagnies inactives
   */
  async list(all?: boolean): Promise<Compagnie[]> {
    const params = all ? { all: 'true' } : {};
    const response = await apiClient.get<Compagnie[]>(BASE_URL, { params });
    return response.data;
  },

  /**
   * Detail d'une compagnie
   */
  async get(id: number): Promise<Compagnie> {
    const response = await apiClient.get<Compagnie>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Creation d'une compagnie
   */
  async create(data: CreateCompagnieDTO): Promise<Compagnie> {
    const response = await apiClient.post<Compagnie>(BASE_URL, data);
    return response.data;
  },

  /**
   * Modification d'une compagnie
   */
  async update(id: number, data: UpdateCompagnieDTO): Promise<Compagnie> {
    const response = await apiClient.put<Compagnie>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Suppression d'une compagnie
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Activer/desactiver une compagnie
   */
  async toggleActif(id: number): Promise<Compagnie> {
    const response = await apiClient.patch<Compagnie>(`${BASE_URL}/${id}/toggle-actif`);
    return response.data;
  },

  /**
   * Statistiques des compagnies
   */
  async getStats(): Promise<CompagnieStats> {
    const response = await apiClient.get<CompagnieStats>(`${BASE_URL}/stats/global`);
    return response.data;
  },

  /**
   * Import depuis un fichier Excel
   */
  async import(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ImportResult>(`${BASE_URL}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default compagnieService;
